/**
 * Privacidade (LGPD) — retenção, legal hold, fila de expurgo e solicitações do titular
 * (AN-12 / L17 · Q41, Onda 5 — DEV-AB).
 *
 * Alinhado aos contratos REAIS repassados pelo ORQ em 25/10: DEV-W (`originei-documentos`,
 * `/api/v1/privacidade/**`, sha edb521a) e DEV-AA (`originei-auth`, `/api/v1/lgpd/**`, sha
 * 62295ea). Ambos com Jackson `non_null`: campo nulo vem AUSENTE no JSON. Ajustes de nome na
 * integração ficam aqui e nos dois serviços (`privacidadeService`, `solicitacoesTitularService`),
 * não nas telas.
 *
 * Regras que a tela só ESPELHA (quem decide é o servidor):
 *  - (R1) prazo de retenção nasce vazio — `prazoDias: null` = "Sem prazo (nunca propõe)";
 *  - (R2) duas aprovações humanas distintas (1ª DPO, 2ª ADMIN) + flag de expurgo físico;
 *  - (R3) legal hold prevalece — conferido na entrada, em cada aprovação e na execução.
 */

/** Papel novo do realm (AN-12 §7.4) — `SecurityRoles.PAPEL_DPO`. */
export const PAPEL_DPO = 'DPO'

// ---------------------------------------------------------------- configuração

/** `GET /v1/privacidade/configuracao` (doc) — flags e carência, só leitura, para o banner. */
export interface ConfiguracaoPrivacidade {
  /** `PRIVACIDADE_EXPURGO_FISICO_HABILITADO` — nasce `false` (ONDA5, decisão 2). */
  expurgoFisicoHabilitado: boolean
  /** `PRIVACIDADE_RETENCAO_VARREDURA_HABILITADA` — nasce `false`. */
  varreduraHabilitada?: boolean
  /** `PRIVACIDADE_EXPURGO_CARENCIA_HORAS` — técnica, provisória (24 h), não é prazo legal. */
  carenciaHoras?: number | null
  /** `PRIVACIDADE_EXPURGO_CONFIRMACOES` — módulos que confirmam a remoção das cópias. */
  confirmacoesEsperadas?: string[]
}

// ---------------------------------------------------------------- política de retenção

export type MarcoRetencao = 'REMOCAO_LOGICA'

/** Uma versão da política de uma categoria (append-only; alterar = nova versão). */
export interface PoliticaRetencao {
  id: string
  /** `tipo_entidade` do documento (OPORTUNIDADE, PENDENCIA, NOTA_FISCAL_COMISSAO…). */
  categoria: string
  /** Refina a categoria; a regra mais específica vence. */
  tipoDocumento?: string | null
  /** `null` = nunca propõe expurgo (R1). */
  prazoDias: number | null
  marco: MarcoRetencao
  /** `false` = obrigação de retenção: não entra na fila nem manualmente. */
  expurgavel: boolean
  versao: number
  vigenteDesde: string
  justificativa: string
  criadaPor: string
  /** Marcado pelo servidor (versão mais nova da sua categoria/tipo) — a tela não calcula. */
  vigente: boolean
}

/** O que a tela recebe do serviço (normalizado — ver `privacidadeService.politicas`). */
export interface PoliticasRetencao {
  vigentes: PoliticaRetencao[]
  historico: PoliticaRetencao[]
}

/**
 * `POST /v1/privacidade/politicas-retencao` — nova versão (só DPO; ADMIN 403). Sem `marco` (o
 * servidor só conhece REMOCAO_LOGICA). `prazoDias` ausente = sem prazo. 409
 * POLITICA_VERSAO_CONCORRENTE.
 */
export interface NovaPoliticaRetencaoRequest {
  /** `[A-Z][A-Z0-9_]{0,39}` */
  categoria: string
  tipoDocumento?: string
  prazoDias?: number
  expurgavel?: boolean
  /** ≤ 500 */
  justificativa: string
}

/** Categorias conhecidas (AN-12 §3) — a lista é sugestão; o servidor aceita as que conhece. */
export const CATEGORIAS_RETENCAO: { valor: string; rotulo: string }[] = [
  { valor: 'OPORTUNIDADE', rotulo: 'Documentos de reserva' },
  { valor: 'PENDENCIA', rotulo: 'Anexos de pendência' },
  { valor: 'NOTA_FISCAL_COMISSAO', rotulo: 'Nota fiscal de comissão' },
  { valor: 'PRODUTO', rotulo: 'Material de produto' },
  { valor: 'CONTEUDO_PUBLICADO', rotulo: 'Conteúdo publicado' },
  { valor: 'ONBOARDING', rotulo: 'Onboarding' },
  { valor: 'CAMPANHA', rotulo: 'Campanha' },
  { valor: 'INCIDENTE', rotulo: 'Incidente' },
]

export const rotuloCategoria = (valor: string): string =>
  CATEGORIAS_RETENCAO.find((c) => c.valor === valor)?.rotulo ?? valor

// ---------------------------------------------------------------- legal hold

export type AlvoRetencaoLegal = 'DOCUMENTO' | 'RESERVA' | 'CNPJ'

export const ALVO_RETENCAO_LABEL: Record<AlvoRetencaoLegal, string> = {
  DOCUMENTO: 'Documento',
  RESERVA: 'Reserva',
  CNPJ: 'CNPJ',
}

export interface RetencaoLegal {
  id: string
  alvoTipo: AlvoRetencaoLegal
  /** uuid canônico (documento/reserva) ou CNPJ só dígitos. */
  alvoRef: string
  motivo: string
  referenciaProcesso?: string | null
  criadaPor: string
  criadaEm: string
  liberadaPor?: string | null
  liberadaEm?: string | null
  motivoLiberacao?: string | null
  ativa?: boolean
  /** Só na resposta da criação: códigos EXP-… que passaram a BLOQUEADO_RETENCAO. */
  expurgosBloqueados?: string[]
}

export interface NovaRetencaoLegalRequest {
  alvoTipo: AlvoRetencaoLegal
  alvoRef: string
  motivo: string
  referenciaProcesso?: string
}

// ---------------------------------------------------------------- fila de expurgo

export type StatusExpurgo =
  | 'PENDENTE'
  | 'APROVADO_1'
  | 'APROVADO'
  | 'EXECUTANDO'
  | 'EM_EXECUCAO'
  | 'AGUARDANDO_COPIAS'
  | 'CONCLUIDO'
  | 'REJEITADO'
  | 'CANCELADO'
  | 'BLOQUEADO_RETENCAO'
  | 'ERRO'

export const STATUS_EXPURGO: StatusExpurgo[] = [
  'PENDENTE',
  'APROVADO_1',
  'APROVADO',
  'EXECUTANDO',
  'AGUARDANDO_COPIAS',
  'CONCLUIDO',
  'REJEITADO',
  'CANCELADO',
  'BLOQUEADO_RETENCAO',
  'ERRO',
]

export const STATUS_EXPURGO_LABEL: Record<StatusExpurgo, string> = {
  PENDENTE: 'Aguardando 1ª aprovação (DPO)',
  APROVADO_1: 'Aguardando 2ª aprovação (ADMIN)',
  APROVADO: 'Aprovado',
  EXECUTANDO: 'Executando',
  EM_EXECUCAO: 'Executando',
  AGUARDANDO_COPIAS: 'Aguardando confirmação das cópias',
  CONCLUIDO: 'Concluído',
  REJEITADO: 'Rejeitado',
  CANCELADO: 'Cancelado',
  BLOQUEADO_RETENCAO: 'Bloqueado por retenção legal',
  ERRO: 'Erro na execução',
}

/**
 * Antes de EXECUTANDO ainda dá para rejeitar (AN-12 §5.2), inclusive na carência. `ERRO` entra:
 * é a conferência da execução que falhou ANTES de apagar qualquer coisa (o servidor recusa com
 * 409 ITEM_POSSIVELMENTE_EXECUTADO se algum item já foi ao storage).
 */
export const STATUS_REJEITAVEIS: StatusExpurgo[] = ['PENDENTE', 'APROVADO_1', 'APROVADO', 'BLOQUEADO_RETENCAO', 'ERRO']

export type OrigemExpurgo = 'TITULAR' | 'POLITICA' | 'MANUAL'

export const ORIGEM_EXPURGO_LABEL: Record<OrigemExpurgo, string> = {
  TITULAR: 'Solicitação do titular',
  POLITICA: 'Política de retenção',
  MANUAL: 'Manual',
}

export type FundamentoExpurgo =
  | 'SOLICITACAO_TITULAR'
  | 'FIM_DA_RETENCAO'
  | 'DETERMINACAO_ANPD_OU_JUDICIAL'
  | 'OUTRO'

export const FUNDAMENTO_EXPURGO_LABEL: Record<FundamentoExpurgo, string> = {
  SOLICITACAO_TITULAR: 'Solicitação do titular',
  FIM_DA_RETENCAO: 'Fim da retenção',
  DETERMINACAO_ANPD_OU_JUDICIAL: 'Determinação da ANPD ou judicial',
  OUTRO: 'Outro',
}

export type MotivoInclusao = 'SELECIONADO' | 'VERSAO_ANTERIOR' | 'COPIA_REUSO'

export const MOTIVO_INCLUSAO_LABEL: Record<MotivoInclusao, string> = {
  SELECIONADO: 'Selecionado',
  VERSAO_ANTERIOR: 'Versão anterior',
  COPIA_REUSO: 'Cópia por reuso',
}

export type MotivoBloqueio =
  | 'DOCUMENTO_ATIVO'
  | 'DOCUMENTO_JA_EXPURGADO'
  | 'DOCUMENTO_NAO_ENCONTRADO'
  | 'CATEGORIA_NAO_EXPURGAVEL'
  | 'DOCUMENTO_EM_RETENCAO_LEGAL'
  | 'DOCUMENTO_EM_OUTRO_EXPURGO'
  | 'RESERVA_NAO_SINCRONIZADA'

export const MOTIVO_BLOQUEIO_LABEL: Record<MotivoBloqueio, string> = {
  DOCUMENTO_ATIVO: 'Documento ativo — remova-o (exclusão lógica) antes de expurgar',
  DOCUMENTO_JA_EXPURGADO: 'Documento já expurgado',
  DOCUMENTO_NAO_ENCONTRADO: 'Documento não encontrado',
  CATEGORIA_NAO_EXPURGAVEL: 'Categoria com obrigação de retenção (não expurgável)',
  DOCUMENTO_EM_RETENCAO_LEGAL: 'Em retenção legal (legal hold)',
  DOCUMENTO_EM_OUTRO_EXPURGO: 'Já está em outro expurgo aberto',
  RESERVA_NAO_SINCRONIZADA: 'Reserva não sincronizada — não dá para conferir retenção legal',
}

export interface ItemPrevia {
  documentoId: string
  /** Nome do arquivo — só enquanto não expurgado; depois vem `null`. */
  nome?: string | null
  tipoDocumento?: string | null
  categoria?: string | null
  entidadeReferencia?: string | null
  versao?: number | null
  motivoInclusao: MotivoInclusao
  tamanhoBytes?: number | null
  reservaId?: string | null
  removidoEm?: string | null
}

export interface BloqueioPrevia {
  documentoId: string
  codigo: MotivoBloqueio | string
  detalhe?: string | null
}

/** `GET /v1/privacidade/expurgos/previa` — não grava. */
export interface PreviaExpurgo {
  itens: ItemPrevia[]
  bloqueios: BloqueioPrevia[]
  /** sha256 dos ids ordenados; cada aprovação é presa a ele. */
  previaHash: string
  /** `false` quando há bloqueio ou nenhum item. */
  expurgavel?: boolean
}

export interface FiltroPrevia {
  documentoIds?: string[]
  reservaId?: string
}

export interface NovoExpurgoRequest extends FiltroPrevia {
  fundamentoTipo: FundamentoExpurgo
  /** ≤ 1000 */
  fundamentoTexto: string
  /** Obrigatório com SOLICITACAO_TITULAR — o protocolo TIT- do auth. */
  solicitacaoProtocolo?: string
  /** A prévia que a pessoa viu; mudou ⇒ 409 PREVIA_DESATUALIZADA. */
  previaHash?: string
}

export type StatusItemExpurgo =
  | 'PENDENTE'
  | 'EXECUTANDO'
  | 'AGUARDANDO_COPIAS'
  | 'CONCLUIDO'
  | 'ERRO'
  | 'REJEITADO'
  | 'CANCELADO'
  /** Retirado pelo DPO de um expurgo em execução (só item que o storage nunca tocou). */
  | 'RETIRADO'

/**
 * Prefixo do `ultimoErro` que a reconciliação grava no item (`AGUARDANDO_CONFIRMACAO:<módulos>`)
 * quando as cópias demoram a confirmar — leitura de campo, sem cálculo na tela.
 */
export const PREFIXO_ALERTA_RECONCILIACAO = 'AGUARDANDO_CONFIRMACAO'

export interface ItemExpurgo extends ItemPrevia {
  status: StatusItemExpurgo
  tentativas?: number
  ultimoErro?: string | null
  /** Módulos que já confirmaram a remoção das cópias (`ia`, `oportunidades`). */
  confirmacoes?: string[]
  expurgadoEm?: string | null
  /** O storage já foi chamado para o item (pode já ter sido apagado): não é retirável. */
  possivelmenteExecutado?: boolean
  retiradoPor?: string | null
  retiradoEm?: string | null
  motivoRetirada?: string | null
}

export interface Expurgo {
  id: string
  /** Código curto que o aprovador digita para confirmar (ex.: EXP-2026-00012). */
  codigo: string
  origem: OrigemExpurgo
  fundamentoTipo: FundamentoExpurgo
  fundamentoTexto: string
  solicitacaoProtocolo?: string | null
  status: StatusExpurgo
  previaHash: string
  solicitadoPor: string
  solicitadoEm: string
  aprovado1Por?: string | null
  aprovado1Em?: string | null
  aprovado2Por?: string | null
  aprovado2Em?: string | null
  rejeitadoPor?: string | null
  rejeitadoEm?: string | null
  motivoRejeicao?: string | null
  canceladoPor?: string | null
  canceladoEm?: string | null
  executarApos?: string | null
  concluidoEm?: string | null
  /** `@Version` — vai na aprovação (409 CONFLITO_VERSAO). */
  versao?: number
  /** Quem dá a próxima aprovação; ausente = nenhuma pendente. O botão segue ISTO. */
  proximaAprovacao?: 'DPO' | 'ADMIN' | null
  /** APROVADO com a flag de expurgo físico desligada. */
  aguardandoHabilitacao?: boolean
  /** Na listagem vem só o total; no detalhe (`GET /{id}`), os itens e a trilha. */
  totalItens?: number
  itens?: ItemExpurgo[]
  trilha?: RegistroExpurgo[]
}

// ---------------------------------------------------------------- trilha de expurgos

export type EventoRegistroExpurgo =
  | 'SOLICITADO'
  | 'APROVADO_1'
  | 'APROVADO_2'
  | 'REJEITADO'
  | 'BLOQUEADO_RETENCAO'
  | 'STORAGE_INICIADO'
  | 'EXECUTADO'
  | 'RETIRADO'
  | 'CONCLUIDO'
  | 'REAPLICADO'
  | string // `COPIA_CONFIRMADA:<modulo>`

/** `registros_expurgo` — append-only, sem PII por construção (sem nome de arquivo, texto, CPF). */
export interface RegistroExpurgo {
  id: string
  expurgoId: string
  documentoId?: string | null
  evento: EventoRegistroExpurgo
  tipoDocumento?: string | null
  tipoEntidade?: string | null
  entidadeReferencia?: string | null
  versao?: number | null
  tamanhoBytes?: number | null
  motivoInclusao?: MotivoInclusao | null
  fundamentoTipo?: FundamentoExpurgo | null
  solicitacaoProtocolo?: string | null
  /** `sub` ou `SISTEMA`. */
  ator: string
  ocorridoEm: string
  detalhe?: string | null
}

// ---------------------------------------------------------------- solicitação do titular (auth)
//
// Contrato REAL do DEV-AA (auth, sha 62295ea), repassado pelo ORQ em 25/10. Jackson `non_null`:
// campo nulo vem AUSENTE no JSON — por isso quase tudo é opcional aqui.

/** `GET /v1/lgpd/configuracao` (qualquer autenticado). */
export interface ConfiguracaoLgpd {
  /** `LGPD_ANONIMIZACAO_IMEDIATA` — `true` hoje (ONDA5, decisão 1). */
  anonimizacaoImediata: boolean
  /** `LGPD_TITULAR_PRAZO_DIAS` — ausente = a tela não mostra prazo (AN-12 A3/P-4). */
  prazoRespostaDias?: number
  /** Sem `LGPD_TITULAR_PEPPER` a identificação por CPF fica desabilitada. */
  identificacaoPorDocumentoHabilitada: boolean
}

export type TipoSolicitacaoTitular =
  | 'ACESSO'
  | 'CORRECAO'
  | 'ANONIMIZACAO'
  | 'ELIMINACAO'
  | 'PORTABILIDADE'
  | 'REVOGACAO_CONSENTIMENTO'
  | 'INFORMACAO'
  | 'OUTRO'

export const TIPO_SOLICITACAO_LABEL: Record<TipoSolicitacaoTitular, string> = {
  ACESSO: 'Acesso aos dados',
  CORRECAO: 'Correção',
  ANONIMIZACAO: 'Anonimização',
  ELIMINACAO: 'Eliminação',
  PORTABILIDADE: 'Portabilidade',
  REVOGACAO_CONSENTIMENTO: 'Revogação de consentimento',
  INFORMACAO: 'Informação',
  OUTRO: 'Outro',
}

export type CanalSolicitacao = 'PORTAL' | 'EMAIL' | 'OUTRO'

export const CANAL_SOLICITACAO_LABEL: Record<CanalSolicitacao, string> = {
  PORTAL: 'Portal',
  EMAIL: 'E-mail',
  OUTRO: 'Outro',
}

export type EstadoSolicitacaoTitular =
  | 'RECEBIDA'
  | 'EM_ANALISE'
  | 'AGUARDANDO_EXECUCAO'
  | 'ATENDIDA'
  | 'NEGADA'
  | 'CANCELADA'

export const ESTADOS_SOLICITACAO: EstadoSolicitacaoTitular[] = [
  'RECEBIDA',
  'EM_ANALISE',
  'AGUARDANDO_EXECUCAO',
  'ATENDIDA',
  'NEGADA',
  'CANCELADA',
]

export const ESTADO_SOLICITACAO_LABEL: Record<EstadoSolicitacaoTitular, string> = {
  RECEBIDA: 'Recebida',
  EM_ANALISE: 'Em análise',
  AGUARDANDO_EXECUCAO: 'Aguardando execução',
  ATENDIDA: 'Atendida',
  NEGADA: 'Negada',
  CANCELADA: 'Cancelada',
}

/**
 * Transições cuja `nota` é o fundamento (400 `FUNDAMENTO_OBRIGATORIO` sem ela) — o corpo da
 * transição é só `{ para, nota? }`.
 */
export const ESTADOS_COM_FUNDAMENTO: EstadoSolicitacaoTitular[] = ['AGUARDANDO_EXECUCAO', 'ATENDIDA', 'NEGADA']

export type SistemaChecklist =
  | 'AUTH_KEYCLOAK'
  | 'DOCUMENTOS'
  | 'IA'
  | 'OPORTUNIDADES'
  | 'CLIENTES'
  | 'NOTIFICACOES'
  | 'AUDITORIA'
  | 'KAFKA_OUTBOX'
  | 'BACKUPS'
  | 'PROVEDOR_LLM'

/** Checklist manual de sistemas (AN-12 §1.2) — dado estruturado fora do expurgo automático. */
export const SISTEMAS_CHECKLIST: { chave: SistemaChecklist; rotulo: string }[] = [
  { chave: 'AUTH_KEYCLOAK', rotulo: 'Usuários / Keycloak' },
  { chave: 'DOCUMENTOS', rotulo: 'Documentos (arquivos) — pela fila de expurgo' },
  { chave: 'IA', rotulo: 'IA (embeddings e análises)' },
  { chave: 'OPORTUNIDADES', rotulo: 'Oportunidades (respostas de formulário, contatos, NF)' },
  { chave: 'CLIENTES', rotulo: 'Clientes (contatos)' },
  { chave: 'NOTIFICACOES', rotulo: 'Notificações' },
  { chave: 'AUDITORIA', rotulo: 'Trilha de auditoria' },
  { chave: 'KAFKA_OUTBOX', rotulo: 'Kafka / outbox' },
  { chave: 'BACKUPS', rotulo: 'Backups (reaplicar após restauração)' },
  { chave: 'PROVEDOR_LLM', rotulo: 'Provedor de LLM' },
]

export interface SistemaVerificado {
  sistema: SistemaChecklist
  verificado: boolean
  observacao?: string
  /** `sub` de quem marcou. */
  por?: string
  em?: string
}

/** `Solicitacao` do auth (visão DPO/ADMIN). */
export interface SolicitacaoTitular {
  id: string
  protocolo: string
  tipo: TipoSolicitacaoTitular
  canal: CanalSolicitacao
  usuarioId?: string
  titularNome?: string
  identificadoPorDocumento: boolean
  /** 2 últimos dígitos do CPF (o CPF em claro nunca é gravado — só HMAC). */
  titularDocumentoFinal?: string
  contatoResposta?: string
  descricao?: string
  recebidaEm: string
  /** Informativo; ausente quando não configurado — a tela não mostra contador. */
  prazoRespostaEm?: string
  responsavelId?: string
  estado: EstadoSolicitacaoTitular
  /** O servidor decide — a tela não calcula. */
  proximosEstados: EstadoSolicitacaoTitular[]
  fundamentoDecisao?: string
  sistemasVerificados: SistemaVerificado[]
  tenantId?: string
  registradaPor: string
  /** `sub` do DPO que pôs em AGUARDANDO_EXECUCAO — o ADMIN que executa precisa ser outro. */
  aguardandoExecucaoPor?: string
  executadaPor?: string
  executadaEm?: string
  atualizadaEm: string
  /** ANONIMIZACAO em AGUARDANDO_EXECUCAO ainda não executada. */
  execucaoAnonimizacaoPendente: boolean
  version: number
}

/** `MinhaSolicitacao` — o que o próprio titular vê (`/minhas`). */
export interface MinhaSolicitacao {
  protocolo: string
  tipo: TipoSolicitacaoTitular
  canal: CanalSolicitacao
  estado: EstadoSolicitacaoTitular
  descricao?: string
  recebidaEm: string
  prazoRespostaEm?: string
  fundamentoDecisao?: string
  atualizadaEm: string
  /** RECEBIDA ou EM_ANALISE — fora disso 409 SOLICITACAO_NAO_CANCELAVEL. */
  cancelavel: boolean
}

export type TipoEventoSolicitacao = 'CRIADA' | 'TRANSICAO' | 'RESPONSAVEL' | 'CHECKLIST' | 'PRAZO' | 'EXECUCAO'

export const TIPO_EVENTO_SOLICITACAO_LABEL: Record<TipoEventoSolicitacao, string> = {
  CRIADA: 'Registrada',
  TRANSICAO: 'Transição',
  RESPONSAVEL: 'Responsável',
  CHECKLIST: 'Checklist',
  PRAZO: 'Prazo',
  EXECUCAO: 'Execução',
}

export interface EventoSolicitacaoTitular {
  id: string
  tipo: TipoEventoSolicitacao | string
  de?: EstadoSolicitacaoTitular
  para?: EstadoSolicitacaoTitular
  /** `sub` de quem agiu. */
  autor?: string
  em: string
  nota?: string
}

/** `POST /v1/lgpd/solicitacoes` — o próprio titular; nome/contato vêm do token. */
export interface NovaSolicitacaoPropriaRequest {
  tipo: TipoSolicitacaoTitular
  descricao?: string
}

/** `POST /v1/lgpd/solicitacoes/registrar` — canal externo, pelo DPO/ADMIN. */
export interface RegistrarSolicitacaoRequest {
  tipo: TipoSolicitacaoTitular
  canal: 'EMAIL' | 'OUTRO'
  titularNome: string
  /** CPF — vai ao servidor só para o HMAC; nunca é exibido de volta. */
  titularDocumento?: string
  contatoResposta: string
  descricao?: string
  /** AAAA-MM-DD; futura ⇒ 400. */
  recebidaEm?: string
  usuarioId?: string
}

export interface TransicaoSolicitacaoRequest {
  para: EstadoSolicitacaoTitular
  /** Fundamento quando `para` ∈ ESTADOS_COM_FUNDAMENTO. */
  nota?: string
}

/**
 * Resposta de `POST /v1/lgpd/anonimizar`:
 *  - flag `true`: 200 `{ status: "anonimizado", mensagem }` (comportamento atual);
 *  - flag `false`: 202 `{ status: "solicitada", protocolo, mensagem }` (idempotente).
 */
export interface ResultadoAnonimizacao {
  status: 'anonimizado' | 'solicitada'
  /** Só quando virou solicitação. */
  protocolo: string | null
  mensagem?: string
}

/**
 * `POST /v1/privacidade/expurgos/reaplicar` (DEV-X, só ADMIN): documentos expurgados conferidos,
 * quantos foram reapagados (voltaram de backup) e quantos falharam.
 */
export interface ReaplicacaoExpurgoResponse {
  verificados: number
  reaplicados: number
  falhas: number
  documentos: string[]
}
